import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspace(data.workspace);
      localStorage.setItem("nl_session", "1");
      return data;
    } catch (e) {
      setUser(false);
      setWorkspace(null);
      localStorage.removeItem("nl_session");
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle the exchange first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip the /auth/me probe on public pages when there's no session hint (avoids 401 noise).
    if (!localStorage.getItem("nl_session") && !window.location.pathname.startsWith("/app") && !window.location.pathname.startsWith("/admin")) {
      setUser(false);
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    localStorage.setItem("nl_session", "1");
    await refresh();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    setWorkspace(data.workspace);
    localStorage.setItem("nl_session", "1");
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e?.message || e);
    }
    setUser(false);
    setWorkspace(null);
    localStorage.removeItem("nl_session");
  };

  const value = {
    user,
    workspace,
    loading,
    login,
    register,
    logout,
    refresh,
    setWorkspace,
    apiError,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
